import styled from 'styled-components';

import { useGetCurrentUser } from '@wecasa/auth/src/authentication-context';

import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { formatPricePerHour } from '@wecasa/common/src/utils/formatting';

import { CONFIG_BY_COUNTRY } from '@wecasa/customer/src/constants/country-config';
import useGetPrices from '@wecasa/customer/src/hooks/use-get-prices';
import type { StandardPrices } from '@wecasa/customer/src/types/prices';
import { getPlatformFees, type PlatformFees } from '@wecasa/customer/src/utils/cleaning';

import { translate } from '@wecasa/i18n';

import { useIsITCSelectedOrSuccessfullyPaired } from '@wecasa/instant-tax-credit-potential-creation-funnel/src/hooks/use-is-itc-selected-or-successfully-paired';

import useHasOrWantsMembership from '@wecasa/membership/src/hooks/use-has-or-wants-membership';

import Text from '@wecasa/web-ui/src/components/base-text/variants/text';
import Skeleton from '@wecasa/web-ui/src/components/skeleton/skeleton';

export const MOBILE_BASKET_CLEANING_PRICES_LOADED_TEST_ID = 'mobile-basket-cleaning-prices-loaded-test-id';

const t = translate('containers.basket');

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const PlatformFeesPrice = ({ platformFees }: { platformFees: PlatformFees }) => (
  <>
    <Text.MDBold>{formatPricePerHour(platformFees.followingSessionsPrice.hourly_price)}</Text.MDBold>
    <div>
      <Text.XS>
        {t('basket.cleaning.services.first_session_subtitle', {
          price: formatPricePerHour(platformFees.firstSessionPrice.hourly_price),
        })}
      </Text.XS>
    </div>
  </>
);

type StandardPriceProps = {
  isITCSelectedOrSuccessfullyPaired: boolean;
  showTaxCreditInBasket: boolean;
  userPrices: StandardPrices;
};

const StandardPrice = ({
  isITCSelectedOrSuccessfullyPaired,
  showTaxCreditInBasket,
  userPrices,
}: StandardPriceProps) => (
  <>
    <Text.MDBold strikedOut={isITCSelectedOrSuccessfullyPaired}>
      {formatPricePerHour(userPrices.total.hourly_price)}
    </Text.MDBold>
    {showTaxCreditInBasket && (
      <div>
        <Text.MDBold>{`${formatPricePerHour(userPrices.after_tax.hourly_price)} `}</Text.MDBold>
        <Text.XS>{t('prices.after_tax')}</Text.XS>
      </div>
    )}
  </>
);

const CleaningPreview = () => {
  const { isTaxInfoDisplayed, isTaxCreditAvailable } = CONFIG_BY_COUNTRY[getCurrentCountry()];

  const user = useGetCurrentUser();
  const { isITCSelectedOrSuccessfullyPaired, shouldShowTaxCreditPriceInfo } = useIsITCSelectedOrSuccessfullyPaired({
    user,
    isCesuEnabled: true,
  });
  // Tax credit only applies when booking for self (legal eligibility)
  const showTaxCreditInBasket = isTaxCreditAvailable && shouldShowTaxCreditPriceInfo;

  const { prices, pricesError } = useGetPrices();
  const hasMembership = useHasOrWantsMembership();
  const userPrices = hasMembership ? prices?.member : prices?.standard;
  const platformFees = getPlatformFees(userPrices);

  return (
    <PriceWrapper>
      {!userPrices || pricesError ? (
        <Skeleton linesWidth={showTaxCreditInBasket ? ['60px', '100px'] : ['60px']} />
      ) : (
        <span data-testid={MOBILE_BASKET_CLEANING_PRICES_LOADED_TEST_ID}>
          {platformFees ? (
            <PlatformFeesPrice platformFees={platformFees} />
          ) : (
            <StandardPrice
              isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
              showTaxCreditInBasket={showTaxCreditInBasket}
              userPrices={userPrices}
            />
          )}
        </span>
      )}
      {isTaxInfoDisplayed && shouldShowTaxCreditPriceInfo && <Text.SM>{t('.tax_credit_info')}</Text.SM>}
    </PriceWrapper>
  );
};

export default CleaningPreview;
